/**
 * Stripe Client Integration
 * Handles checkout sessions and billing portal access
 */

import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { functions } from './firebase';
import { getUserClaimsWithCache } from './optimizedAuth';

const auth = getAuth();

/**
 * Create creator subscription checkout session
 * @deprecated Subscriptions are no longer available. Platform uses pay-per-use model.
 * This function is kept for legacy compatibility only. New code should not use this.
 * Use createLaunchCheckout() for pay-per-use launches instead.
 * @param {string} plan - The plan type ('creator_lite' or 'creator_pro')
 * @param {string} origin - The origin URL for success/cancel redirects
 * @return {Promise<string>} Stripe checkout URL
 */
export const createCreatorCheckout = async (plan = 'creator_lite', origin = window.location.origin) => {
  console.warn(
    '⚠️ DEPRECATED: createCreatorCheckout() is deprecated. ' +
    'Platform now uses pay-per-use model ($15 per launch). ' +
    'This function is kept for legacy compatibility only.'
  );
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Get fresh ID token
    const idToken = await user.getIdToken(false);

    // Call the HTTP endpoint directly
    const response = await fetch('https://us-central1-trivia-plug-3.cloudfunctions.net/createCreatorCheckoutHTTP', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ plan, origin }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Function response:', response.status, errorText);
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const result = await response.json();
    return result.url;
  } catch (error) {
    console.error('Error creating creator checkout:', error);
    throw new Error(error.message || 'Failed to create checkout session');
  }
};

/**
 * Create game launch checkout session
 * @param {string} gameId - The game ID to purchase launch rights for
 * @param {string} gameTitle - The game title for display
 * @param {string} origin - The origin URL for success/cancel redirects
 * @return {Promise<string>} Stripe checkout URL
 */
export const createLaunchCheckout = async (gameId, gameTitle, origin = window.location.origin) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Ensure user has a fresh token
    await user.getIdToken(false);

    const createCheckout = httpsCallable(functions, 'createLaunchCheckoutSession');
    const result = await createCheckout({ 
      gameId, 
      gameTitle,
      origin 
    });
    
    return result.data.url;
  } catch (error) {
    console.error('Error creating launch checkout:', error);
    throw new Error(error.message || 'Failed to create checkout session');
  }
};

/**
 * Create checkout session for $15 created game launch
 * @param {string} gameId - The game ID to launch
 * @return {Promise<string>} Stripe checkout URL
 */
export const createCreatedGameLaunchCheckout = async (gameId) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Ensure user has a fresh token
    await user.getIdToken(false);

    const createCheckout = httpsCallable(functions, 'createCreatedGameLaunchCheckout');
    const result = await createCheckout({ gameId });
    
    return result.data.url;
  } catch (error) {
    console.error('Error creating $15 game launch checkout:', error);
    throw new Error(error.message || 'Failed to create checkout session');
  }
};

/**
 * Create billing portal session for subscription management
 * @param {string} returnUrl - URL to return to after billing portal
 * @return {Promise<string>} Stripe billing portal URL
 */
export const createBillingPortal = async (returnUrl = `${window.location.origin}/account-settings`) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Ensure user has a fresh token
    await user.getIdToken(false);

    const createPortal = httpsCallable(functions, 'createBillingPortalSession');
    const result = await createPortal({ returnUrl });
    
    return result.data.url;
  } catch (error) {
    console.error('Error creating billing portal:', error);
    throw new Error(error.message || 'Failed to create billing portal session');
  }
};

/**
 * Check if user has active creator subscription
 * @deprecated Subscriptions are no longer available. Platform uses pay-per-use model.
 * This function is kept for legacy users only. New code should not use this.
 * @return {Promise<boolean>} True if user has active creator subscription
 */
// Cache for creator subscription status to prevent excessive token requests
let creatorSubscriptionCache = {
  value: null,
  timestamp: 0,
  ttl: 30000 // 30 seconds cache
};

export const hasActiveCreatorSubscription = async () => {
  console.warn(
    '⚠️ DEPRECATED: hasActiveCreatorSubscription() is deprecated. ' +
    'Platform now uses pay-per-use model. This function is kept for legacy users only.'
  );
  try {
    const user = auth.currentUser;
    if (!user) {
      return false;
    }

    // Check cache first
    const now = Date.now();
    if (creatorSubscriptionCache.value !== null && 
        (now - creatorSubscriptionCache.timestamp) < creatorSubscriptionCache.ttl) {
      return creatorSubscriptionCache.value;
    }

    // Get claims with caching (reduces quota usage)
    try {
      const claims = await getUserClaimsWithCache(false); // Don't force refresh
      const result = claims.creator === true && claims.creatorActive === true;
      
      // Cache the result
      creatorSubscriptionCache = {
        value: result,
        timestamp: now,
        ttl: 30000
      };
      
      return result;
    } catch (tokenError) {
      console.error('Error checking creator subscription:', tokenError);
      // Cache false result to prevent repeated failures
      creatorSubscriptionCache = {
        value: false,
        timestamp: now,
        ttl: 10000 // Shorter cache for failures
      };
      return false;
    }
  } catch (error) {
    console.error('Error checking creator subscription:', error);
    return false;
  }
};

/**
 * Check if user has launch entitlement for a specific game
 * @param {string} gameId - The game ID to check
 * @return {Promise<boolean>} True if user has valid launch entitlement
 */
export const hasLaunchEntitlement = async (gameId) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return false;
    }

    // Check if user is a creator (can launch any game)
    if (await hasActiveCreatorSubscription()) {
      return true;
    }

    // Check Firestore for specific launch entitlement
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    
    const entitlementDoc = await getDoc(doc(db, `entitlements/${user.uid}/launches/${gameId}`));
    
    if (!entitlementDoc.exists()) {
      return false;
    }

    const entitlement = entitlementDoc.data();
    const now = new Date();
    const expiresAt = entitlement.expiresAt?.toDate();

    return entitlement.status === 'active' && expiresAt && expiresAt > now;
  } catch (error) {
    console.error('Error checking launch entitlement:', error);
    return false;
  }
};

/**
 * Get subscription status details
 * @deprecated Subscriptions are no longer available. Platform uses pay-per-use model.
 * This function is kept for legacy users only. New code should not use this.
 * @return {Promise<object|null>} Subscription details or null
 */
export const getSubscriptionStatus = async () => {
  console.warn(
    '⚠️ DEPRECATED: getSubscriptionStatus() is deprecated. ' +
    'Platform now uses pay-per-use model. This function is kept for legacy users only.'
  );
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    
    const subscriptionDoc = await getDoc(doc(db, `subscriptions/${user.uid}/creator/meta`));
    
    if (!subscriptionDoc.exists()) {
      return null;
    }

    const subscription = subscriptionDoc.data();
    return {
      active: subscription.active,
      currentPeriodEnd: subscription.currentPeriodEnd?.toDate(),
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
      updatedAt: subscription.updatedAt?.toDate()
    };
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return null;
  }
};
